import { guid } from '../../../../utils/guid';
import { formatDayMonthYear } from '../../../../utils/date-only';
import { CreatedOrder, Order, ORDER_STATUS_LABELS, OrderItem, OrderStatus } from './order.dto';
import { BomStructure } from '../../../../models/bom-structure';

export interface OrderVm {
  /**
   * 'new' while the page is a fresh `orders/new` whose placeholder rows have not created
   * the zlecenie yet (the first position does — 0039).
   */
  id: number | 'new';
  createdAt: string;
  orderNumber: string;
  name: string;
  status: OrderStatus;
  statusLabel: string;
  /** "07.09.2026 – 21.09.2026" once planned; null until the planner sets the window. */
  plannedLabel: string | null;
  deadline: string | null;
  /** 0..1 — the API's, the same value the planner bar and the board card fill by (0112). */
  progressFraction: number;
  items: OrderItemVm[];
}

export namespace OrderVm {
  export function mapModelToViewModel(order: Order): OrderVm {
    return {
      id: order.id,
      createdAt: order.createdAt,
      orderNumber: order.orderNumber,
      name: order.name,
      status: order.status,
      statusLabel: ORDER_STATUS_LABELS[order.status],
      plannedLabel: order.plannedStartDate && order.plannedEndDate
        ? `${formatDayMonthYear(order.plannedStartDate)} – ${formatDayMonthYear(order.plannedEndDate)}`
        : null,
      deadline: order.deadline,
      progressFraction: order.progressFraction,
      items: order.items.map(OrderItemTreeVm.mapModelToViewModel)
    };
  }

  // a just-created order: the header from the response over a fresh order's empty planning fields
  export function mapCreatedToViewModel(created: CreatedOrder): OrderVm {
    return {
      ...draft(),
      id: created.id,
      createdAt: created.createdAt,
      orderNumber: created.orderNumber,
      name: created.name,
      status: created.status,
      statusLabel: ORDER_STATUS_LABELS[created.status],
      items: created.items.map(OrderItemTreeVm.mapModelToViewModel)
    };
  }

  /**
   * The not-yet-saved order a fresh page shows its placeholder rows in — header
   * fields are empty until ensureOrder replaces it with the created one.
   */
  export function draft(): OrderVm {
    return {
      id: 'new',
      createdAt: '',
      orderNumber: '',
      name: '',
      status: OrderStatus.new,
      statusLabel: ORDER_STATUS_LABELS[OrderStatus.new],
      plannedLabel: null,
      deadline: null,
      progressFraction: 0,
      items: []
    };
  }
}

export interface OrderItemVm {
  /**
   * Guid or another unique temp id for presentational purposes.
   */
  id: string;
  data: OrderItemData;
  children: OrderItemVm[];
}

export interface OrderItemData {
  orderItemId?: number;
  /**
   * Nullable in the schema (the snapshot owns its name); absent on a client-sourced
   * placeholder, whose position the bank does not describe by BOM item.
   */
  bomItemId?: number;
  name: string;
  /**
   * The snapshot's unit — what amount is counted in; printed after it (0069).
   */
  unit: string;
  /**
   * BOM-style amount: the ordered quantity on a root, per-one-parent on a child —
   * the same meaning as in the BOM tree, so the two trees read alike and
   * tree-summary multiplies it down the tree into totals exactly as it does there.
   */
  amount: number;
  /**
   * The zamówienie a client-sourced root covers (0014); null on ad-hoc items and children.
   */
  clientOrder: OrderItemClientOrder | null;
  /**
   * The position's thumbnail (0081) — frozen at creation, else the BOM item's current
   * photo; null when there is none, and on rows the create responses replaced.
   */
  primaryPhotoAttachmentId: number | null;
  /**
   * The line's frozen structure (0120) — absent on placeholder rows not yet created.
   */
  structure?: BomStructure;
}

export interface OrderItemClientOrder {
  clientOrderId: number;
  orderNumber: string;
  clientName: string;
}

export namespace OrderItemTreeVm {
  export function mapModelToViewModel(orderItem: OrderItem): OrderItemVm {
    return {
      id: guid(),
      data: {
        orderItemId: orderItem.orderItemId,
        bomItemId: orderItem.bomItemId ?? undefined,
        name: orderItem.name,
        unit: orderItem.unit,
        amount: orderItem.perParentAmount ?? orderItem.amount,
        clientOrder: orderItem.allocation
          ? {
              clientOrderId: orderItem.allocation.clientOrderId,
              orderNumber: orderItem.allocation.clientOrderNumber,
              clientName: orderItem.allocation.clientName
            }
          : null,
        primaryPhotoAttachmentId: orderItem.primaryPhotoAttachmentId ?? null,
        structure: orderItem.structure
      },
      children: orderItem.children.map(mapModelToViewModel)
    };
  }
}
